import { ChevronRight24Regular, Home24Regular, Microscope24Filled, Search24Filled } from "@fluentui/react-icons";
import { useEffect, useState } from "react";
import { GetEnumerationSet } from "../../../services/OiService";
import { useEventReport } from "../redux/ReduxContext";

export interface SelectedTest {
    name: string;
    fullPath: string;
    path: string[];
    isLeafNode: boolean;
}



export default function TestSelection() {

    const [poiHierarchy, setPOIHierarchy] = useState<POINode | null>(null);
    const [currentPath, setCurrentPath] = useState<string[]>([]);
    const [breadcrumbPath, setBreadcrumbPath] = useState<string[]>([]);
    const [currentOptions, setCurrentOptions] = useState<string[]>([]);
    const [selected, setSelected] = useState<string | null>(null);
    const [isLeafNodeSelected, setIsLeafNodeSelected] = useState(false);

    const [searchQuery, setSearchQuery] = useState("");
    const [searchSuggestions, setSearchSuggestions] = useState<SearchResult[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [allPaths, setAllPaths] = useState<SearchResult[]>([]);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
    const [isInitialTestFound, setIsInitialTestFound] = useState(false);

    const { state, dispatch } = useEventReport();
    const { testSelection } = state;


    useEffect(() => {
        const initializeEnumData = async () => {
            try {
                const data = await GetEnumerationSet();
                if (data) {
                    const hierarchy = createPOIHierarchy(data);
                    setPOIHierarchy(hierarchy);
                    const topLevelOptions = Array.from(hierarchy.children.keys());
                    setCurrentOptions(topLevelOptions);
                    const paths = collectAllPaths(hierarchy);
                    setAllPaths(paths);
                }
            } catch (error) {
                console.error("Failed to load enumeration set:", error);
            }
        };
        initializeEnumData();

    }, [testSelection]);

    useEffect(() => {
        findTest(testSelection)
    }, [testSelection, allPaths, poiHierarchy]);



    const findTest = (testName: string) => {

        if (testName) {
            let foundTest = null;

            foundTest = allPaths.find(path =>
                path.path[path.path.length - 1] === testName && path.isLeaf
            );

            if (!foundTest) {
                foundTest = allPaths.find(path =>
                    path.fullPath === testName && path.isLeaf
                );
            }

            if (!foundTest) {
                foundTest = allPaths.find(path =>
                    path.path[path.path.length - 1].toLowerCase() === testName.toLowerCase() && path.isLeaf
                );
            }

            if (!foundTest) {
                foundTest = allPaths.find(path =>
                    path.fullPath.toLowerCase() === testName.toLowerCase() && path.isLeaf
                );
            }

            if (!foundTest) {
                foundTest = allPaths.find(path =>
                    path.isLeaf && (
                        path.path.some(part => part.toLowerCase().includes(testName.toLowerCase())) ||
                        path.fullPath.toLowerCase().includes(testName.toLowerCase())
                    )
                );
            }

            if (foundTest) {
                navigateToPath(foundTest.path);
                setIsInitialTestFound(true);
            } else {
                setIsInitialTestFound(false);
            }
        } else if (!testName && poiHierarchy) {
            goToRoot();
            setIsInitialTestFound(false);
        }

    }

    const navigateToPath = (path: string[]) => {
        if (!poiHierarchy) return;
        let currentNode = poiHierarchy;
        for (const pathPart of path) {
            const nextNode = currentNode.children.get(pathPart);
            if (!nextNode) return;
            currentNode = nextNode;
        }

        const childrenOptions = Array.from(currentNode.children.keys());
        const isLeafNode = childrenOptions.length === 0;
        setCurrentPath(path);
        if (isLeafNode && path.length > 0) {
            setBreadcrumbPath(path.slice(0, -1));
            setSelected(path[path.length - 1]);
            setIsLeafNodeSelected(true);
            setIsInitialTestFound(true);

            const selectedTest: SelectedTest = {
                name: path[path.length - 1],
                fullPath: path.join(" > "),
                path: [...path],
                isLeafNode: true
            };
            dispatch({ type: 'SET_FIELD', payload: { field: 'testSelection', value: selectedTest.name } });
            dispatch({ type: 'SET_FIELD', payload: { field: 'elementName', value: selectedTest.name } });

        } else {
            setBreadcrumbPath(path.slice(0, -1));
            setSelected(path.length > 0 ? path[path.length - 1] : null);
            setIsLeafNodeSelected(false);
            dispatch({ type: 'SET_FIELD', payload: { field: 'testSelection', value: null } });
            dispatch({ type: 'SET_FIELD', payload: { field: 'elementName', value: null } });
        }

        setCurrentOptions(childrenOptions);
    };
    const handleSearchChange = (value: string) => {
        setSearchQuery(value);
        setSelectedSuggestionIndex(-1);

        if (value.trim().length < 2) {
            setSearchSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        const query = value.toLowerCase();
        const filtered = allPaths.filter(result =>
            result.fullPath.toLowerCase().includes(query) ||
            result.path.some(part => part.toLowerCase().includes(query))
        ).slice(0, 10);

        setSearchSuggestions(filtered);
        setShowSuggestions(filtered.length > 0);
    };

    const handleSearchSelect = (result: SearchResult) => {
        navigateToPath(result.path);
        setSearchQuery(result.fullPath);
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent) => {
        if (!showSuggestions || searchSuggestions.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedSuggestionIndex(prev =>
                    prev < searchSuggestions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < searchSuggestions.length) {
                    handleSearchSelect(searchSuggestions[selectedSuggestionIndex]);
                }
                break;
            case 'Escape':
                setShowSuggestions(false);
                setSelectedSuggestionIndex(-1);
                break;
        }
    };

    const handleSearchBlur = () => {
        setTimeout(() => {
            setShowSuggestions(false);
            setSelectedSuggestionIndex(-1);
        }, 200);
    };

    const handleSearchFocus = () => {
        if (searchQuery.trim().length >= 2 && searchSuggestions.length > 0) {
            setShowSuggestions(true);
        }
    };

    const handleNavigationClick = (option: string) => {
        const newPath = [...currentPath, option];
        navigateToPath(newPath);
        setSearchQuery("");
    };

    const handleBreadcrumbClick = (index: number) => {
        const newPath = currentPath.slice(0, index + 1);
        navigateToPath(newPath);
        setSearchQuery("");
    };

    const goToRoot = () => {
        setCurrentPath([]);
        setBreadcrumbPath([]);
        setSelected(null);
        setIsLeafNodeSelected(false);
        setSearchQuery("");
        dispatch({ type: 'SET_FIELD', payload: { field: 'testSelection', value: null } });
        dispatch({ type: 'SET_FIELD', payload: { field: 'elementName', value: null } });
        if (poiHierarchy) {
            const topLevelOptions = Array.from(poiHierarchy.children.keys());
            setCurrentOptions(topLevelOptions);
        }
    };

    const clearSelection = () => {
        goToRoot();
    };

    interface POINode {
        name: string;
        children: Map<string, POINode>;
        parent?: POINode;
    }

    const createPOIHierarchy = (enumData: Record<number, string>): POINode => {
        const root: POINode = { name: "Root", children: new Map() };

        Object.values(enumData).forEach(item => {
            const parts = item.split('-').map(part => part.trim()).filter(part => part.length > 0);
            if (parts.length === 0) return;

            let currentNode = root;

            for (const partName of parts) {
                if (!currentNode.children.has(partName)) {
                    const newNode: POINode = {
                        name: partName,
                        children: new Map(),
                        parent: currentNode
                    };
                    currentNode.children.set(partName, newNode);
                }
                currentNode = currentNode.children.get(partName)!;
            }
        });

        return root;
    };

    const collectAllPaths = (node: POINode, currentPath: string[] = []): SearchResult[] => {
        const results: SearchResult[] = [];

        if (currentPath.length > 0) {
            const isLeaf = node.children.size === 0;
            results.push({
                path: [...currentPath],
                fullPath: currentPath.join(" > "),
                isLeaf
            });
        }

        node.children.forEach((childNode, childName) => {
            const childPath = [...currentPath, childName];
            results.push(...collectAllPaths(childNode, childPath));
        });

        return results;
    };

    interface SearchResult {
        path: string[];
        fullPath: string;
        isLeaf: boolean;
    }
    return (
        <div className="bg-white dark:bg-oi-dark-background p-4 mb-4 drop-shadow-md">
            <div className="flex items-center justify-between">
                <h6>
                    Find your test instrument
                </h6>
                <div className="flex items-center gap-2">
                    {testSelection && isLeafNodeSelected && (
                        <button
                            onClick={clearSelection}
                            className="text-xs px-3 py-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                        >
                            Clear Selection
                        </button>
                    )}
                    {testSelection && (
                        <span className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                            Edit Mode
                        </span>
                    )}
                </div>
            </div>

            {testSelection && !isInitialTestFound && allPaths.length > 0 && (
                <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                        <strong>Warning:</strong> Test instrument "{testSelection}" not found. Please select a different test.
                    </p>
                </div>
            )}

            <div className="relative w-full">
                <input
                    type="text"
                    placeholder="Search test instruments..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    onFocus={handleSearchFocus}
                    onBlur={handleSearchBlur}
                    className="h-12 w-full p-2 pr-10 mt-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500  border border-gray-300 dark:border-gray-600  bg-primary-light dark:bg-primary-dark"
                />
                <button
                    type="button"
                    className="absolute right-2 top-8 -translate-y-1/2 text-gray-600 hover:text-blue-600"
                >
                    <Search24Filled />
                </button>

                {showSuggestions && searchSuggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto bg-primary-light dark:bg-primary-dark" >
                        {searchSuggestions.map((suggestion, index) => (
                            <button
                                key={index}
                                onClick={() => handleSearchSelect(suggestion)}
                                className={`w-full px-3 py-2 text-left text-sm border-b border-gray-200 dark:border-gray-600 last:border-b-0 flex items-center justify-between transition-colors ${index === selectedSuggestionIndex
                                    ? 'bg-blue-100 dark:bg-blue-900'
                                    : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                                    }`}
                            >
                                <span className="flex-1">{suggestion.fullPath}</span>
                                {suggestion.isLeaf && (
                                    <span className="text-xs text-green-600 dark:text-green-400 ml-2 px-2 py-1 bg-green-100 dark:bg-green-900 rounded">
                                        Selectable
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <h6 className="font-semibold mt-4">Or start here:</h6>
            <div className="max-sm:text-xs md:text-md flex flex-wrap items-center gap-2 bg-gray-100 dark:bg-gray-700 my-4">
                <button
                    onClick={goToRoot}
                    className="flex items-center gap-1 py-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900 rounded"
                >
                    <Home24Regular className="w-4 h-4" />
                    Home
                </button>
                {breadcrumbPath.map((crumb, index) => (
                    <div key={index} className="flex items-center gap min-w-0">
                        <ChevronRight24Regular className="w-2 h-2 text-gray-400" />
                        <button
                            onClick={() => handleBreadcrumbClick(index)}
                            className="px-1 py-1 min-w-0 truncate hover:text-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900 rounded"
                        >
                            {crumb}
                        </button>
                    </div>
                ))}
                {selected && (
                    <div className="flex items-center gap-2 min-w-0">
                        <ChevronRight24Regular className="w-4 h-4 text-gray-400" />
                        <span className="px-2 py-1 text-gray-800 dark:text-gray-200 truncate">
                            {selected}
                        </span>
                    </div>
                )}
            </div>


            {currentOptions.length > 0 && (
                <div className="w-0 min-w-full overflow-hidden">
                    <div className="flex flex-wrap gap-4">
                        {currentOptions.map((option) => (
                            <button
                                key={option}
                                onClick={() => handleNavigationClick(option)}
                                className="px-4 py-3 bg-oi-button-blue text-primary-light inline-flex items-center gap-2 max-w-full"
                            >
                                <span className="truncate">{option}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {!isLeafNodeSelected && (
                <div className="text-left pt-4 text-gray-500">
                    <p className="text-bad-red">*Mandatory field</p>
                </div>
            )}

            {currentOptions.length === 0 && !selected && (
                <div className="text-center py-4 text-gray-500">
                    <p>No test instruments available</p>
                </div>
            )}
        </div>
    );
}